import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiExternalLink, FiLink, FiPlus, FiTrash2 } from "react-icons/fi";
import { SiGoogledocs, SiGooglesheets, SiSignal, SiWhatsapp } from "react-icons/si";
import http from "../api/apiClient";
import Modal from "./Modal";

type ClassroomLink = {
  id: string;
  title: string;
  url: string;
  description?: string | null;
};

type Overview = {
  links?: ClassroomLink[];
  canManage?: boolean;
};

type LinkForm = {
  title: string;
  url: string;
  description: string;
};

const emptyForm: LinkForm = { title: "", url: "", description: "" };

export const linkKindForUrl = (value: string): "signal" | "whatsapp" | "google-docs" | "google-sheets" | "generic" => {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (host === "signal.group" || host.endsWith(".signal.org") || host === "signal.me") return "signal";
    if (host === "wa.me" || host === "whatsapp.com" || host.endsWith(".whatsapp.com")) return "whatsapp";
    if (host === "docs.google.com" && url.pathname.startsWith("/spreadsheets/")) return "google-sheets";
    if (host === "docs.google.com" && url.pathname.startsWith("/document/")) return "google-docs";
  } catch {
    return "generic";
  }
  return "generic";
};

export const LinkIcon = ({ url }: { url: string }) => {
  const className = "h-5 w-5 shrink-0";
  switch (linkKindForUrl(url)) {
    case "signal": return <SiSignal aria-hidden="true" className={className} />;
    case "whatsapp": return <SiWhatsapp aria-hidden="true" className={className} />;
    case "google-docs": return <SiGoogledocs aria-hidden="true" className={className} />;
    case "google-sheets": return <SiGooglesheets aria-hidden="true" className={className} />;
    default: return <FiExternalLink aria-hidden="true" className={className} />;
  }
};

const ClassLinksSection: React.FC<{
  classroomId?: string | number | null;
  embedded?: boolean;
  schoolWide?: boolean;
  allowManagement?: boolean;
}> = ({
  classroomId,
  embedded = false,
  schoolWide = false,
  allowManagement = true,
}) => {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<Overview>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<ClassroomLink | null>(null);
  const [form, setForm] = useState<LinkForm>(emptyForm);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!schoolWide && !classroomId) return;
    setLoading(true);
    setError("");
    try {
      const endpoint = schoolWide ? "/school-links" : `/classrooms/${classroomId}/links`;
      const { data } = await http.get<Overview>(endpoint);
      setOverview(data || {});
    } catch {
      setError(t("classrooms.links.load_failed", { defaultValue: "Important links could not be loaded." }));
    } finally {
      setLoading(false);
    }
  }, [classroomId, schoolWide, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (link: ClassroomLink) => {
    setEditing(link);
    setForm({ title: link.title, url: link.url, description: link.description || "" });
    setError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!schoolWide && !classroomId) return;
    setSaving(true);
    setError("");
    const payload = {
      title: form.title.trim(),
      url: form.url.trim(),
      description: form.description.trim() || null,
    };
    try {
      const endpoint = schoolWide ? "/school-links" : `/classrooms/${classroomId}/links`;
      if (editing) {
        await http.put(`${endpoint}/${editing.id}`, payload);
      } else {
        await http.post(endpoint, payload);
      }
      setModalOpen(false);
      setEditing(null);
      setForm(emptyForm);
      await load();
    } catch {
      setError(t("classrooms.links.save_failed", { defaultValue: "The link could not be saved." }));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (linkId: string) => {
    if (!schoolWide && !classroomId) return;
    setSaving(true);
    setError("");
    try {
      const endpoint = schoolWide ? "/school-links" : `/classrooms/${classroomId}/links`;
      await http.delete(`${endpoint}/${linkId}`);
      await load();
    } catch {
      setError(t("classrooms.links.remove_failed", { defaultValue: "The link could not be removed." }));
    } finally {
      setSaving(false);
    }
  };

  if (!schoolWide && !classroomId) return null;

  return (
    <section className={embedded ? "mt-5" : "rounded-2xl border border-slate-200/80 bg-white p-5 dark:border-white/10 dark:bg-white/5"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FiLink aria-hidden="true" className="text-emerald-600 dark:text-emerald-300" />
          <h2 className="text-base font-bold text-slate-950 dark:text-white">
            {schoolWide
              ? t("school.links.title", { defaultValue: "School-wide links" })
              : t("classrooms.links.title", { defaultValue: "Class links" })}
          </h2>
        </div>
        {overview.canManage && allowManagement ? (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-emerald-600 px-3 text-sm font-bold text-white transition hover:bg-emerald-700"
          >
            <FiPlus aria-hidden="true" />
            {t("classrooms.links.add", { defaultValue: "Add link" })}
          </button>
        ) : null}
      </div>

      {loading ? (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          {t("common.loading", { defaultValue: "Loading..." })}
        </p>
      ) : (overview.links || []).length ? (
        <div className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-100 dark:divide-slate-800 dark:border-slate-800">
          {(overview.links || []).map((link) => (
            <div key={link.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700 hover:underline dark:text-emerald-300"
                >
                  <LinkIcon url={link.url} />
                  <span className="truncate">{link.title}</span>
                </a>
                {link.description ? (
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{link.description}</p>
                ) : null}
              </div>
              {overview.canManage && allowManagement ? (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => openEdit(link)}
                    aria-label={t("classrooms.links.edit", { defaultValue: "Edit {{title}}", title: link.title })}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <FiEdit2 aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => remove(link.id)}
                    aria-label={t("classrooms.links.remove", { defaultValue: "Remove {{title}}", title: link.title })}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full text-red-600 transition hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
                  >
                    <FiTrash2 aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          {t("classrooms.links.empty", { defaultValue: "No important links have been added yet." })}
        </p>
      )}

      {error ? <p role="alert" className="mt-3 text-sm font-semibold text-red-600 dark:text-red-300">{error}</p> : null}

      <Modal
        show={modalOpen}
        onClose={closeModal}
        title={editing
          ? t("classrooms.links.edit_title", { defaultValue: "Edit link" })
          : t("classrooms.links.add_title", { defaultValue: "Add link" })}
        type="manage"
        variant="lds"
        hideDefaultActions
        dialogClassName="sm:max-w-lg"
      >
        <form onSubmit={save} className="space-y-4 text-left">
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-200">
            {t("classrooms.links.fields.title", { defaultValue: "Title" })}
            <input
              autoFocus
              required
              maxLength={150}
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-slate-950 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </label>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-200">
            {t("classrooms.links.fields.url", { defaultValue: "URL" })}
            <input
              required
              type="url"
              maxLength={2048}
              placeholder="https://"
              value={form.url}
              onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
              className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-slate-950 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </label>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-200">
            {t("classrooms.links.fields.description", { defaultValue: "Description (optional)" })}
            <textarea
              maxLength={500}
              rows={3}
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-950 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </label>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={closeModal} className="min-h-11 rounded-xl border border-slate-300 px-4 text-sm font-bold dark:border-slate-700">
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button disabled={saving} className="min-h-11 rounded-xl bg-emerald-600 px-5 text-sm font-bold text-white disabled:opacity-50">
              {saving
                ? t("common.saving", { defaultValue: "Saving..." })
                : t("common.save", { defaultValue: "Save" })}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
};

export default ClassLinksSection;
